/**
 * HTML Sanitization Utility
 *
 * Provides secure HTML sanitization using DOMPurify to prevent XSS attacks
 * while preserving safe HTML formatting for dynamic forms and content.
 *
 * @module htmlSanitizer
 * @version 1.0.0
 */

import DOMPurify from 'dompurify';

/**
 * Configuration profiles for different use cases
 */
export type SanitizeProfile = 'strict' | 'form' | 'basic';

// Per-session cache to avoid re-running DOMPurify on the same HTML string.
// Key: `${profile}:${html}`, value: sanitized result.
const sanitizeCache = new Map<string, string>();

/**
 * DOMPurify configuration for different security levels
 */
const SANITIZE_CONFIGS: Record<SanitizeProfile, DOMPurify.Config> = {
  /**
   * STRICT: Maximum security - only basic formatting
   * Use for: User-generated content, untrusted sources (tenant names, emails)
   */
  strict: {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'br', 'p', 'span'],
    ALLOWED_ATTR: ['class'],
    KEEP_CONTENT: true,
    RETURN_TRUSTED_TYPE: false,
  },

  /**
   * FORM: Balanced security for form labels and options
   * Use for: Form templates, labels, tooltips from API
   */
  form: {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'br', 'p', 'span', 'a', 'ul', 'li', 'ol', 'div'],
    ALLOWED_ATTR: ['class', 'href', 'target', 'rel'],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    KEEP_CONTENT: true,
    RETURN_TRUSTED_TYPE: false,
  },

  /**
   * BASIC: Minimal sanitization - preserve most HTML
   * Use for: Trusted admin content, CMS content
   */
  basic: {
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'link', 'form', 'input'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout', 'onmousemove', 'onfocus', 'onblur'],
    KEEP_CONTENT: true,
    RETURN_TRUSTED_TYPE: false,
  },
};

/**
 * Sanitize HTML string using DOMPurify
 *
 * @param html - The HTML string to sanitize
 * @param profile - Security profile to use (default: 'form')
 * @param customConfig - Optional custom DOMPurify configuration
 * @returns Sanitized HTML string safe for rendering
 *
 * @example
 * ```typescript
 * // Form labels (default)
 * const safeLabel = sanitizeHtml('<b>Name</b>: <script>alert("xss")</script>');
 * // Returns: '<b>Name</b>: '
 *
 * // Strict mode for user content
 * const safeName = sanitizeHtml(userInput, 'strict');
 *
 * // Custom configuration
 * const safeHtml = sanitizeHtml(html, 'form', { ALLOWED_TAGS: ['b', 'i'] });
 * ```
 */
export function sanitizeHtml(
  html: string | null | undefined,
  profile: SanitizeProfile = 'form',
  customConfig?: DOMPurify.Config
): string {
  // Handle null/undefined gracefully
  if (html === null || html === undefined) {
    return '';
  }

  // Handle non-string values
  if (typeof html !== 'string') {
    console.warn('[HTMLSanitizer] Non-string value provided:', typeof html);
    return String(html);
  }

  // Empty string check
  if (html.trim() === '') {
    return '';
  }

  // Get base configuration for profile
  const baseConfig = SANITIZE_CONFIGS[profile];

  // Only cache when no custom config override (custom configs are caller-specific)
  const cacheKey = customConfig ? null : `${profile}:${html}`;
  if (cacheKey && sanitizeCache.has(cacheKey)) {
    return sanitizeCache.get(cacheKey)!;
  }

  // Merge with custom config if provided
  const config = customConfig ? { ...baseConfig, ...customConfig } : baseConfig;

  try {
    // Sanitize and return
    const sanitized = DOMPurify.sanitize(html, config);

    // Log if content was modified (development only) — once per unique string
    if (process.env.NODE_ENV === 'development' && sanitized !== html) {
      console.warn('[HTMLSanitizer] Content was sanitized:', {
        original: html.substring(0, 100) + (html.length > 100 ? '...' : ''),
        sanitized: sanitized.substring(0, 100) + (sanitized.length > 100 ? '...' : ''),
        profile: profile,
        removed: html.length - sanitized.length + ' characters',
      });
    }

    if (cacheKey) sanitizeCache.set(cacheKey, sanitized);
    return sanitized;
  } catch (error) {
    console.error('[HTMLSanitizer] Sanitization failed:', error);
    // Fail-safe: return empty string if sanitization fails
    // This is safer than returning potentially malicious content
    return '';
  }
}

/**
 * Convenience function for strict sanitization
 * Use for user-generated content (tenant names, emails, etc.)
 *
 * @param html - HTML string to sanitize
 * @returns Strictly sanitized HTML string
 */
export const sanitizeStrict = (html: string | null | undefined): string =>
  sanitizeHtml(html, 'strict');

/**
 * Convenience function for form content sanitization
 * Use for form labels, options, tooltips from API
 *
 * @param html - HTML string to sanitize
 * @returns Form-sanitized HTML string
 */
export const sanitizeForm = (html: string | null | undefined): string =>
  sanitizeHtml(html, 'form');

/**
 * Convenience function for basic sanitization
 * Use for trusted admin content
 *
 * @param html - HTML string to sanitize
 * @returns Basically sanitized HTML string
 */
export const sanitizeBasic = (html: string | null | undefined): string =>
  sanitizeHtml(html, 'basic');

/**
 * Helper to create safe dangerouslySetInnerHTML props
 * This is the preferred method for rendering sanitized HTML in React
 *
 * @param html - HTML string to sanitize
 * @param profile - Security profile (default: 'form')
 * @returns Object ready for dangerouslySetInnerHTML prop
 *
 * @example
 * ```typescript
 * // Clean API
 * <div {...createSafeHTML(label)} />
 *
 * // Instead of:
 * // <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(label) }} />
 * ```
 */
export function createSafeHTML(
  html: string | null | undefined,
  profile: SanitizeProfile = 'form'
): { dangerouslySetInnerHTML: { __html: string } } {
  return {
    dangerouslySetInnerHTML: {
      __html: sanitizeHtml(html, profile),
    },
  };
}

/**
 * Check if DOMPurify is available and working
 * Useful for diagnostics and testing
 *
 * @returns true if DOMPurify is functional
 */
export function isSanitizerAvailable(): boolean {
  try {
    const testHtml = '<b>test</b><script>alert(1)</script>';
    const result = DOMPurify.sanitize(testHtml);
    return result === '<b>test</b>';
  } catch {
    return false;
  }
}
